import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalObj, LoadingPage, selectAllOnFocus, SetState } from "./Common";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { Client } from "./Clients";
import { CREATE_CLIENT_DETAIL_ID, Quote } from "./Quotes";
import { Container, FormControl, InputLabel, List, ListItem, ListItemButton, MenuItem, Popover, Select } from "@mui/material";
import { Add, Edit, PersonAdd } from "@mui/icons-material";
import { UserContext, userIsEmployeeOrAdmin, UserRoleAdmin } from "./Users";

export interface ClientDetails {
    id: number;
	client_id: number;
	person: string;
	contact: string;
	address: string;
}

function QuotesPopover({ details }: { details: ClientDetails }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const auth = useContext(UserContext);
    return (
        userIsEmployeeOrAdmin(auth)
        ? <Fragment>
            <Button aria-describedby="Quotes" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Quotes
            </Button>
            <Popover
                id="Quotes"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/client-details/" + details.id + "/quotes"}> Show Quotes </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton onClick={(ev) => navigate("/quotes/0", { state: {clientDetailIdText: details.id || 0} })}> Create Quote </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
        : <Button aria-describedby="Quotes" component={Link} to={"/client-details/" + details.id + "/quotes"} variant="contained" fullWidth>
            Quotes
        </Button>
    )
}

export function ClientDetailsTable({clientDetails, client}: {clientDetails: Array<ClientDetails>, client: Partial<Client>}) {
    const navigate = useNavigate();
    const auth = useContext(UserContext);

    return (
        <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <h1>{client.name + " (" + client.quote_id + ")"}</h1>
            </Grid>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">Contact Name</TableCell>
                                    <TableCell width="20%">Contact Number</TableCell>
                                    <TableCell width="30%">Address</TableCell>
                                    <TableCell width="15%">
                                    </TableCell>
                                    {auth.user.role === UserRoleAdmin && <TableCell width="5%">
                                        <Button variant="contained" onClick={() => navigate("/client-details/0", { state: { clientId: client.id }})} fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientDetails.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.person}</TableCell>
                                        <TableCell>{row.contact}</TableCell>
                                        <TableCell>{row.address}</TableCell>
                                        <TableCell>
                                            <QuotesPopover details={row} />
                                        </TableCell>
                                        {auth.user.role === UserRoleAdmin && <TableCell>
                                            <Button variant="contained" onClick={() => navigate("/client-details/" + row.id, { state: { clientId: client.id }})} fullWidth>
                                                <Edit />
                                            </Button>
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const ClientDetailsPage = () => {
    const { clientIdText } = useParams();
    const [clientDetails, setClientDetails] = useState([]);
    const [client, setClient] = useState<Partial<Client>>({});
    const [snackbar, setSnackbar] = useState("");

    const clientId = parseInt(clientIdText || "0");

    useEffect(() => {
        const fetchClientDetails = async () => {
            const clientResponse = await fetch("/api/v1/clients/" + clientId);
            const clientData = await clientResponse.json();
            setClient(clientData.result || {});
            if (clientData.error) {
                setSnackbar(clientData.error);
            }

            const detailsResponse = await fetch("/api/v1/client_details?client_id=" + clientId);
            const detailsData = await detailsResponse.json();
            setClientDetails(detailsData.result || []);
            if (clientData.error) {
                setSnackbar(detailsData.error);
            }
        };
        fetchClientDetails();
    }, [clientId]);

    return (
        <Box>
            <EkasiServeAppBar />
            <ClientDetailsTable clientDetails={clientDetails} client={client} />
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Box>
    )
}

export function ClientDetailForm({ clientDetail, setClientDetail } : { clientDetail: Partial<ClientDetails>, setClientDetail: SetState<Partial<Client>> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    id="person"
                    label="Person Name"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClientDetail(clientDetail => ({ ...clientDetail, person: e.target.value }))}
                    fullWidth
                    value={clientDetail.person || ""}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    id="contact"
                    label="Contact Number"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClientDetail(clientDetail => ({ ...clientDetail, contact: e.target.value }))}
                    fullWidth
                    value={clientDetail.contact || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
                <TextField
                    id="address"
                    label="Address"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClientDetail(clientDetail => ({ ...clientDetail, address: e.target.value }))}
                    fullWidth
                    value={clientDetail.address || ""} />
            </Grid>
        </Grid>
    )
}

export const ClientDetailPage = () => {
    const { clientDetailIdText } = useParams();
    const { state } = useLocation();
    const [clientDetail, setClientDetail] = useState<Partial<ClientDetails>>({});
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [client, setClient] = useState<Partial<Client>>({});
    const [savedClientDetail, setSavedClientDetail] = useState<Partial<Client>>({});
    const [edited, setEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();

    const clientId = state?.clientId || 0;
    const clientDetailId = parseInt(clientDetailIdText || "0");

    useEffect(() => {
        const fetchClientDetail = async () => {
            const clientResponse = await fetch("/api/v1/clients/" + clientId);
            const clientData = await clientResponse.json();
            setClient(clientData.result || {});
            if (clientData.error) {
                setSnackbar(clientData.error);
            }

            const detailResponse = await fetch("/api/v1/client_details/" + clientDetailId);
            const detailData = await detailResponse.json();
            if (detailData.result) {
                detailData.result.client_detail.client_id = clientId;
            }
            setClientDetail(detailData.result?.client_detail || {});
            setSavedClientDetail(detailData.result?.client_detail || {});
            if (detailData.error) {
                setSnackbar(detailData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchClientDetail();
    }, [clientDetailId, clientId]);

    useEffect(() => {
        setEdited(!equalObj(clientDetail, savedClientDetail));
    }, [clientDetail, savedClientDetail]);

    const saveClientDetail = useCallback(async () => {
        const response = await fetch("/api/v1/client_details", {
            method: clientDetail.id === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(clientDetail)
        });
        const data = await response.json();
        setClientDetail(data.result || {});
        setSavedClientDetail(data.result || {});
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (clientDetailId === 0) {
                navigate("/client-details/" + data.result.id, { replace: true, state: { clientId: clientId } });
            }
            setSnackbar("Information Saved");
        }
    }, [clientDetail, clientDetailId, navigate, clientId]);

    const deleteClientDetail = useCallback(async () => {
        const response = await fetch("/api/v1/client_details/" + clientDetail.id, { method: "DELETE" });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setClientDetail(data.result || {});
            navigate(-1);
        }
    }, [clientDetail.id, navigate]);

    if (isLoading) return (<LoadingPage />)

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 8 }}><h1>{client.name + " (" + client.quote_id + ")"}</h1></Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    {((clientDetail.id || 0) === 0)
                        ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                        : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <Button disabled={!edited} variant="contained" onClick={saveClientDetail} fullWidth> Save </Button>
                </Grid>
                <Grid size={12}>
                    <ClientDetailForm clientDetail={clientDetail} setClientDetail={setClientDetail} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this client detail? </DialogTitle>
                <Button onClick={deleteClientDetail}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}

export function ClientDetailsSelect({ clientDetails, quote, setQuote } : { clientDetails: Array<ClientDetails>, quote: Partial<Quote>, setQuote: SetState<Partial<Quote>> }) {
    return (
        <FormControl fullWidth>
            <InputLabel>Client Detail</InputLabel>
            <Select
                label="Client Detail"
                value={clientDetails.length === 0 ? "" : (quote.client_detail_id || "")}
                error={(clientDetails.length === 0 ? "" : (quote.client_detail_id || "")) === ""}
                onChange={(ev) => setQuote(quote => ({ ...quote, client_detail_id: ev.target.value as number }))}
            >
                {clientDetails.map((clientDetail) => (
                    <MenuItem value={clientDetail.id}>{clientDetail.person} ({clientDetail.contact}) {clientDetail.address}</MenuItem>
                ))}
                <MenuItem value={CREATE_CLIENT_DETAIL_ID}><PersonAdd fontSize="small" /></MenuItem>
            </Select>
        </FormControl>
    )
}